import { MatchupTeamEntry, Player, PlayerPick, PlayerStats, TeamFixture, TeamFixtureId } from "../models";
import { deepClone } from "./clone";

export const STANDARD_TEAM_COUNT = 11;
export const FULL_SQUAD_COUNT = 15;

export const POSITION_GK = 1;
export const POSITION_DEF = 2;
export const POSITION_MID = 3;
export const POSITION_FWD = 4;

/** Minimum number of each position a lineup must field. */
const MIN_BY_POSITION: Record<number, number> = { [POSITION_GK]: 1, [POSITION_DEF]: 3, [POSITION_MID]: 2, [POSITION_FWD]: 1 };
/** Maximum number of each outfield position a lineup may field. */
const MAX_BY_POSITION: Record<number, number> = { [POSITION_DEF]: 5, [POSITION_MID]: 5, [POSITION_FWD]: 3 };

export type ChipCode = "bboost" | "3xc" | "freehit" | "wildcard";

export const chipLabel = (chip: string | null): string => {
	switch (chip) {
		case "bboost":
			return "Bench Boost";
		case "3xc":
			return "Triple Captain";
		case "freehit":
			return "Free Hit";
		case "wildcard":
			return "Wildcard";
		default:
			return "-";
	}
};

/** A fully scored FPL entry for one gameweek. */
export type EntryScore = {
	id: number;
	players: PlayerPick[];
	totalMinutes: number;
	currentMinutes: number;
	gamesFinished: number;
	gamesNotStarted: number;
	gamesInProgress: number;
	chip: string;
	chipCode: string | null;
	totalPoints: number;
	playerCount: number;
	basePoints: number;
};

/** Builds the picked player with their live points, games and provisional bonus folded in. */
export const buildPlayerPick = (pick: { element: number; is_captain: boolean; is_vice_captain: boolean }, pickPosition: number, players: Player[], playerStats: PlayerStats[], fixtureStats: TeamFixture[]): PlayerPick | null => {
	const playerInfo = players.find((x) => x.id === pick.element);
	let playerPoints = playerStats.find((x) => x.id === pick.element);

	if (playerInfo == null || playerPoints == null) {
		return null;
	}

	const gameInfo = fixtureStats.filter((x) => x.id === playerInfo.team);

	const bonusStats: TeamFixtureId[] = gameInfo.reduce((acc, g) => {
		const foundBonus = g.bps?.find((x) => x.element === pick.element);
		if (foundBonus != null) {
			acc.push({ ...foundBonus, id: g.fixtureId });
		}
		return acc;
	}, [] as TeamFixtureId[]);

	if (bonusStats.length > 0) {
		const calculatedPlayerPoints = deepClone(playerPoints);
		calculatedPlayerPoints.data.forEach((m) => {
			const bonusGame = bonusStats.find((x) => x.id === m.fixture);
			const bonusPoints = bonusGame?.score ?? 0;

			if (bonusGame != null) {
				m.stats.push({ identifier: "bonus", points: bonusPoints, value: bonusPoints, points_modification: 0 });
			}
			calculatedPlayerPoints.points += bonusPoints;
		});
		playerPoints = calculatedPlayerPoints;
	}

	const playedMinutes = playerPoints.data.reduce((prev, current) => prev + (current.stats.find((x) => x.identifier === "minutes")?.value ?? 0), 0);
	const allGamesStarted = gameInfo.every((x) => x.started);
	const gameCurrentMinutes = gameInfo.reduce((prev, current) => prev + current.minutes, 0);
	const totalMinutes = gameInfo.length * 90;

	const canBeSwapped = (playedMinutes === 0 && allGamesStarted) || (playedMinutes === 0 && playerInfo.ineligable);

	return {
		...playerInfo,
		...playerPoints,
		games: gameInfo,
		currentMinutes: gameCurrentMinutes,
		totalMinutes,
		captain: pick.is_captain,
		viceCaptain: pick.is_vice_captain,
		points: playerPoints.points,
		canBeSwapped,
		active: 1,
		pickPosition,
	};
};

/**
 * Applies FPL automatic substitutions in place. Players are in pick order: the first
 * eleven are the chosen starters and the rest the bench in priority order. A starter
 * who cannot play is replaced by the first bench player that keeps the formation legal.
 */
export const applyAutoSubs = (players: PlayerPick[]) => {
	const lineupPlayers = players.slice(0, STANDARD_TEAM_COUNT);
	const benchedPlayers = players.slice(STANDARD_TEAM_COUNT);

	let keeperCount = lineupPlayers.filter((x) => x.type === POSITION_GK && !x.canBeSwapped).length;
	let defenderCount = lineupPlayers.filter((x) => x.type === POSITION_DEF && !x.canBeSwapped).length;
	let midfieldCount = lineupPlayers.filter((x) => x.type === POSITION_MID && !x.canBeSwapped).length;
	let forwardCount = lineupPlayers.filter((x) => x.type === POSITION_FWD && !x.canBeSwapped).length;

	let includedPlayerCount = 0;

	players.forEach((p, index) => {
		// A full team is already fielded, so everyone left sits out
		if (includedPlayerCount >= STANDARD_TEAM_COUNT) {
			p.active = 0;
			return;
		}

		if (!p.canBeSwapped) {
			const benchPlayer = benchedPlayers.some((x) => x.id === p.id);
			let includePlayerInTeam = !benchPlayer;

			if (benchPlayer) {
				const currentPlayerType = p.type;

				// Positions the lineup is still short of
				const requiredPositions: number[] = [];
				if (keeperCount < MIN_BY_POSITION[POSITION_GK]) {
					requiredPositions.push(POSITION_GK);
				}
				if (defenderCount < MIN_BY_POSITION[POSITION_DEF]) {
					requiredPositions.push(...Array(MIN_BY_POSITION[POSITION_DEF] - defenderCount).fill(POSITION_DEF));
				}
				if (midfieldCount < MIN_BY_POSITION[POSITION_MID]) {
					requiredPositions.push(...Array(MIN_BY_POSITION[POSITION_MID] - midfieldCount).fill(POSITION_MID));
				}
				if (forwardCount < MIN_BY_POSITION[POSITION_FWD]) {
					requiredPositions.push(...Array(MIN_BY_POSITION[POSITION_FWD] - forwardCount).fill(POSITION_FWD));
				}

				const addPlayerToActiveTeam = () => {
					switch (currentPlayerType) {
						case POSITION_GK:
							if (keeperCount === 0) {
								includePlayerInTeam = true;
							}
							break;
						case POSITION_DEF:
							if (defenderCount < MAX_BY_POSITION[POSITION_DEF]) {
								includePlayerInTeam = true;
								defenderCount++;
							}
							break;
						case POSITION_MID:
							if (midfieldCount < MAX_BY_POSITION[POSITION_MID]) {
								includePlayerInTeam = true;
								midfieldCount++;
							}
							break;
						case POSITION_FWD:
							if (forwardCount < MAX_BY_POSITION[POSITION_FWD]) {
								includePlayerInTeam = true;
								forwardCount++;
							}
							break;
						default:
							break;
					}
				};

				if (requiredPositions.length > 0) {
					if (requiredPositions.some((x) => x === currentPlayerType)) {
						addPlayerToActiveTeam();
					} else {
						// Not a required position: only play if the bench players after this one
						// can still fill every required slot with substitutions to spare
						const remainingPlayersToCheck = players.slice(index);
						const remainingSubsToMake = STANDARD_TEAM_COUNT - keeperCount - defenderCount - midfieldCount - forwardCount;

						let forcedSubsAfterThis = 0;
						remainingPlayersToCheck.forEach((n) => {
							const requiredIndex = requiredPositions.findIndex((x) => x === n.type);
							if (requiredIndex > -1) {
								forcedSubsAfterThis++;
								requiredPositions.splice(requiredIndex, 1);
							}
						});

						if (remainingSubsToMake > forcedSubsAfterThis) {
							addPlayerToActiveTeam();
						}
					}
				} else {
					addPlayerToActiveTeam();
				}
			}

			if (includePlayerInTeam) {
				includedPlayerCount++;
				p.active = 1;
				return;
			}
		}

		p.active = 0;
	});
};

/** Passes the armband to the vice captain if needed and applies the multiplier in place. */
export const applyCaptaincy = (players: PlayerPick[], chip: string | null) => {
	let captainedPlayer = players.find((x) => x.captain);
	if (captainedPlayer?.canBeSwapped) {
		captainedPlayer.captain = false;
		captainedPlayer = players.find((x) => x.viceCaptain && !x.canBeSwapped);
		if (captainedPlayer) {
			captainedPlayer.captain = true;
		}
	}

	if (captainedPlayer != null && !captainedPlayer.canBeSwapped) {
		const multiplier = chip === "3xc" ? 3 : 2;
		captainedPlayer.points *= multiplier;
	}
};

/** Starters first, then by position, then by name. */
export const playerSorter = (a: PlayerPick, b: PlayerPick) => {
	if (a.active === b.active) {
		if (a.type === b.type) {
			const nameA = a.name.toUpperCase();
			const nameB = b.name.toUpperCase();
			if (nameA < nameB) {
				return -1;
			}
			if (nameA > nameB) {
				return 1;
			}
			return 0;
		}
		return a.type - b.type;
	}
	return b.active - a.active;
};

/** Scores one entry: builds picks, makes substitutions, applies the captain, and totals. */
export const scoreEntry = (entry: MatchupTeamEntry, players: Player[], playerStats: PlayerStats[], fixtureStats: TeamFixture[]): EntryScore => {
	const teamCount = entry.chip === "bboost" ? FULL_SQUAD_COUNT : STANDARD_TEAM_COUNT;

	const picks: PlayerPick[] = entry.picks.reduce((acc, pick, index) => {
		const built = buildPlayerPick(pick, index + 1, players, playerStats, fixtureStats);
		if (built) {
			acc.push(built);
		}
		return acc;
	}, [] as PlayerPick[]);

	if (teamCount === STANDARD_TEAM_COUNT) {
		applyAutoSubs(picks);
	}

	applyCaptaincy(picks, entry.chip);

	picks.sort(playerSorter);

	const selectedPlayers = picks.filter((x) => x.active === 1);

	return {
		id: entry.id,
		players: picks,
		totalMinutes: selectedPlayers.reduce((prev, current) => prev + current.totalMinutes, 0),
		currentMinutes: selectedPlayers.reduce((prev, current) => prev + current.currentMinutes, 0),
		gamesFinished: selectedPlayers.reduce((prev, current) => prev + current.games.filter((x) => x.finished).length, 0),
		gamesNotStarted: selectedPlayers.reduce((prev, current) => prev + current.games.filter((x) => !x.started).length, 0),
		gamesInProgress: selectedPlayers.reduce((prev, current) => prev + current.games.filter((x) => x.started && !x.finished).length, 0),
		chip: chipLabel(entry.chip),
		chipCode: entry.chip,
		totalPoints: selectedPlayers.reduce((prev, current) => prev + current.points, 0) + entry.basePoints,
		playerCount: selectedPlayers.length,
		basePoints: entry.basePoints,
	};
};
